import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { bridge } from "@multiweb/reactCore/bridge";
import { createRefreshAction } from "@multiweb/reactCore/datasource";
import { DIContainer } from "Global/Base/DIContainer";
import { IListener } from "Global/Base/Events";
import { AnyConstructor, Mixin } from "Global/Base/types";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { AutoRefresher } from "UI/UI/UI/AutoRefresher";
import { Control } from "../../UI/UI/Controls/Control";
import { IReactFrameConnector } from "../types";
import { IHasAutorefresh } from "./types";

const { dispatchAction } = bridge;

const logger = LazyLoadingSectionLogger.create(LoggingSection.frames);

/**
 * Mixin, který přidává autorefresh na ConnectorFrame.
 *
 * @param Base Base třída rozšiřující IConnectorFrame.
 */
export const HasAutorefresh = <T extends AnyConstructor<IReactFrameConnector>>(Base: T) =>
    class HasAutorefresh extends Base implements IHasAutorefresh {
        public _autoRefreshListener: IListener | undefined;
        public _autoRefreshInterval: number;
        public _autoRefresher: AutoRefresher;

        constructor(...args: any[]) {
            super(...args);

            this._autoRefreshInterval = Number(this.element.getAttribute("data-autorefresh"));
        }

        public override async initializeVisual(): Promise<void> {
            await super.initializeVisual();

            if (this._autoRefreshInterval > 0) {
                await this._activateAutoRefresh();
            }
        }

        public async _activateAutoRefresh(): Promise<void> {
            if (!(this instanceof Control)) {
                logger.log(`Can not activate AutoRefresh on Frame with id '${this.element.id}'`);
                return;
            }

            ModelDescriptionsManager.instance.getClassInfo(this.objectName).then(classInfo => {
                logger.log(`Activate AutoRefresh on Frame with id '${this.element.id}'`);
                const classNames = classInfo.childClasses.concat(classInfo.relatedClasses, this.objectName);
                this._autoRefresher = new AutoRefresher(this as unknown as Control, classNames, DIContainer.instance.create(IDataEventsManager), this._autoRefreshInterval);
                this._autoRefresher.onRefresh.add((this._autoRefreshListener = () => this._onAutoRefresh()));
            });
        }

        public _onAutoRefresh(): void {
            dispatchAction(createRefreshAction(this.element.id));
        }

        /** @inheritDoc */
        public resetRefreshTimeout(): void {
            this._autoRefresher?.resetRefreshTimeout();
        }

        public override dispose(): void {
            if (this._autoRefresher) {
                logger.log(`Dispose AutoRefresh on Framw with id '${this.element.id}'`);
                if (this._autoRefreshListener) {
                    this._autoRefresher.onRefresh.remove(this._autoRefreshListener);
                }
                this._autoRefresher.dispose();
            }
            super.dispose();
        }
    };

export type HasAutorefresh = Mixin<typeof HasAutorefresh>;
